/**
 * Message « en direct » des pages publiques : quelqu'un vient de donner,
 * réserver, adhérer ou acheter pendant que le visiteur regarde la page.
 *
 * SOURCE UNIQUE du style (Karim 2026-10-10) : la pastille bleue de la page
 * cagnotte, désormais partout — cagnotte, page de l'association, billetterie,
 * adhésion, boutique. Avant, seules la cagnotte (pastille bleue) et la
 * billetterie (toast vert générique) avaient un message, chacune le sien.
 */
import { formatRelativeTime } from "@donoor/utils";
import { useDemoAccueil } from "~/composables/useDemoAccueil";
import type { DerniereActivitePublique } from "@donoor/types";

/** Un toast « en direct » affiché par `ToastsEnDirect` (en bas à gauche). */
export interface ToastEnDirect {
  id: number;
  titre: string;
  quand: string;
}

/** La file affichée (partagée entre toutes les pages). */
export function useToastsEnDirectListe() {
  return useState<ToastEnDirect[]>("toasts-en-direct", () => []);
}

/** Durée d'affichage d'un toast « en direct ». */
const DUREE_MS = 6000;

export function useToastEnDirect() {
  const liste = useToastsEnDirectListe();
  const { t } = useI18n();
  // Page affichée dans la démo de la home : pas d'annonce « en direct » sur
  // la home pour l'association de la démo.
  const { estIntegree } = useDemoAccueil();

  /**
   * Notification « en direct » (Karim 2026-10-13 : donner l'impression qu'un
   * don vient d'être fait) : carte blanche EN BAS À GAUCHE (`ToastsEnDirect`,
   * monté dans app.vue) avec la mascotte Donoor qui lance des cœurs, la
   * phrase en gras, et dessous le moment, précédé d'un point vert.
   *
   * @param titre texte court, ex. « Paul Moret vient de donner 20 € »
   * @param _icone ancienne icône (réservation, adhésion…) : la mascotte la remplace partout
   * @param quand moment de l'activité (« il y a 2 heures ») ; « À l'instant » par défaut
   */
  function annoncerEnDirect(titre: string, _icone = "i-lucide-heart", quand?: string) {
    if (estIntegree) return;
    const moment = quand ?? t("commun.enDirect.alInstant");
    const id = Date.now() + Math.random();
    // Trois au plus à l'écran : le plus ancien laisse sa place.
    liste.value = [...liste.value.slice(-2), { id, titre, quand: moment }];
    setTimeout(() => {
      liste.value = liste.value.filter((x) => x.id !== id);
    }, DUREE_MS);
  }

  return { annoncerEnDirect };
}

/** Au-delà, le nom du donateur est réduit à son prénom. */
const NOM_MAX = 20;

/**
 * Nom du donateur tel qu'affiché dans le message en direct (Karim
 * 2026-10-22) : en entier s'il est court (« Paul Moret »), sinon le prénom
 * seul (« Abdelrahmane Ben Mohamed » → « Abdelrahmane »), coupé « … » si
 * le prénom lui-même est trop long. Vide → null (« Quelqu'un »).
 */
export function nomEnDirect(nom: string | null | undefined): string | null {
  const complet = (nom ?? "").trim().replace(/\s+/g, " ");
  if (!complet) return null;
  if (complet.length <= NOM_MAX) return complet;
  const prenom = complet.split(" ")[0]!;
  return prenom.length <= NOM_MAX ? prenom : `${prenom.slice(0, NOM_MAX - 1)}…`;
}

/**
 * Phrase d'un DON (cagnotte, association) : « Paul Moret vient de donner
 * 20 € » si c'est récent, sinon « Paul Moret a donné 20 € » ; sans montant
 * public, « … faire un don » ; sans nom public (don anonyme, liste des
 * donateurs masquée), « Quelqu'un… ». Textes communs `commun.enDirect.*`.
 */
export function phraseDon(
  t: (cle: string, params?: Record<string, unknown>) => string,
  montant: string | null,
  recente: boolean,
  nomDonateur?: string | null,
): string {
  const nom = nomEnDirect(nomDonateur);
  if (nom) {
    if (montant) return t(recente ? "commun.enDirect.nomVientDeDonnerMontant" : "commun.enDirect.nomADonneMontant", { nom, montant });
    return t(recente ? "commun.enDirect.nomVientDeDonner" : "commun.enDirect.nomADonne", { nom });
  }
  if (montant) return t(recente ? "commun.enDirect.vientDeDonnerMontant" : "commun.enDirect.aDonneMontant", { montant });
  return t(recente ? "commun.enDirect.vientDeDonner" : "commun.enDirect.aDonne");
}

/** « 1 billet » / « 3 billets » — accord du nom avec la quantité. */
export function quantiteEnDirect(n: number, singulier: string, pluriel: string): string {
  return `${n} ${n > 1 ? pluriel : singulier}`;
}

/** En dessous : l'activité est annoncée comme venant d'arriver (« vient de donner »). */
export const ACTIVITE_RECENTE_MS = 15 * 60 * 1000;

/**
 * À l'ARRIVÉE sur la page : annonce la dernière activité si elle a moins de
 * 7 jours (filtre fait par l'API), avec son moment (« il y a 12 heures »).
 * `titre` reçoit `recente` (moins de 15 min) : « vient de donner » n'est
 * écrit que si c'est vrai, sinon « a donné » (Karim 2026-10-13).
 * Le temps réel ne montre que ce qui se passe pendant la visite ; ceci montre
 * qu'il y a de l'activité même quand on arrive plus tard (Karim 2026-10-10).
 *
 * Une seule fois par visite (même activité = même annonce, pas répétée de
 * page en page), côté navigateur, un court instant après l'affichage (pas
 * par-dessus le chargement). Jamais en aperçu dashboard.
 */
export function useAnnonceDerniereActivite(
  source: () => DerniereActivitePublique | null | undefined,
  titre: (activite: DerniereActivitePublique, recente: boolean) => string,
  icone = "i-lucide-heart",
) {
  const { annoncerEnDirect } = useToastEnDirect();
  const route = useRoute();

  onMounted(() => {
    if (route.query.preview === "true") return;
    // Une seule annonce ; le watcher, créé dans onMounted, s'arrête avec la
    // page.
    let annonce = false;
    watch(
      source,
      (activite) => {
        if (annonce || !activite?.at) return;
        const quand = formatRelativeTime(activite.at);
        if (!quand) return;
        annonce = true;
        // Déjà annoncée pendant cette visite (vitrine → produit → vitrine…) :
        // on ne la répète pas à chaque page.
        const recente = Date.now() - new Date(activite.at).getTime() < ACTIVITE_RECENTE_MS;
        const texte = titre(activite, recente);
        const cle = `donoor:derniere-activite:${texte}:${activite.at}`;
        try {
          if (sessionStorage.getItem(cle)) return;
          sessionStorage.setItem(cle, "1");
        } catch {
          // Stockage indisponible (navigation privée) : on annonce quand même.
        }
        setTimeout(() => annoncerEnDirect(texte, icone, quand), 1200);
      },
      { immediate: true },
    );
  });
}
